import React, {useEffect, useRef} from 'react';
import {v4 as uuid} from 'uuid';
import {Body, Headline, Modal} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';

export type CancelSubscriptionModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  planName: string;
  cycleEndDate: Date | null;
  source: string;
  isLoading?: boolean;
  primaryButtonClassName?: string;
  contentClassName?: string;
  isTrial: boolean;
};

function CancelSubscriptionModalContent({
  planName,
  cycleEndDate,
  contentClassName,
}: {
  planName: string;
  cycleEndDate: Date | null;
  contentClassName?: string;
}) {
  const formattedCycleEndDate = cycleEndDate
    ? cycleEndDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  return (
    <div className={contentClassName}>
      <Headline size="md">
        {translate('cancel-subscription-title', {planName})}
      </Headline>
      <Body size="md">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: translate('cancel-subscription-description', {
              type: planName,
              date: formattedCycleEndDate,
            }),
          }}
        />
      </Body>
    </div>
  );
}

function CancelTrialModalContent({
  cycleEndDate,
  contentClassName,
}: {
  cycleEndDate: Date | null;
  contentClassName?: string;
}) {
  const formattedCycleEndDate = cycleEndDate
    ? cycleEndDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  return (
    <div className={contentClassName}>
      <Headline size="md">{translate('Cancel Free Trial')}</Headline>
      <Body size="md">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: translate('cancel-trial-description', {
              date: formattedCycleEndDate,
            }),
          }}
        />
      </Body>
    </div>
  );
}

export default function CancelSubscriptionModal(
  props: CancelSubscriptionModalProps
) {
  const {
    open,
    onClose,
    onConfirm,
    planName,
    cycleEndDate,
    isLoading = false,
    primaryButtonClassName,
    contentClassName,
    source,
    isTrial,
  } = props;
  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {uiInteraction} = useUiInteractionEvent();
  const presentationId = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      presentationId.current = uuid();
      viewPresented({
        view_name: CONTENT_LAB_VIEW_NAMES.CANCEL_SUBSCRIPTION_MODAL,
        view_type: 'modal',
        view_location: CONTENT_LAB_VIEW_NAMES.CANCEL_SUBSCRIPTION_MODAL,
        presentation_id: presentationId.current as string,
        source,
      });
    }
  }, [open]);

  const handleCloseWithAnalytics = () => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'close_cancel_subscription_modal',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CANCEL_SUBSCRIPTION_MODAL,
      interaction_id: interactionId,
    });
    viewDismissed({
      presentation_id: presentationId.current as string,
      interaction_id: interactionId,
      reason: 'dismissed',
    });
    onClose();
  };

  const handleConfirmWithAnalytics = () => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'confirm_cancel_subscription',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CANCEL_SUBSCRIPTION_MODAL,
      interaction_id: interactionId,
    });
    onConfirm();
  };

  return (
    <Modal
      open={open}
      width="444px"
      showCloseButton
      handleClose={handleCloseWithAnalytics}
      handleSecondaryButtonClick={handleCloseWithAnalytics}
      primaryButtonText={
        isTrial
          ? translate('Cancel Free Trial')
          : translate('cancel-subscription-modal-primary-button-text', {
              planName,
            })
      }
      primaryButtonProps={{
        className: primaryButtonClassName,
        disabled: isLoading,
      }}
      handlePrimaryButtonClick={handleConfirmWithAnalytics}
      secondaryButtonText={
        isTrial ? translate("Don't cancel") : translate('Go back')
      }
      size="large"
    >
      {isTrial ? (
        <CancelTrialModalContent
          cycleEndDate={cycleEndDate}
          contentClassName={contentClassName}
        />
      ) : (
        <CancelSubscriptionModalContent
          planName={planName}
          cycleEndDate={cycleEndDate}
          contentClassName={contentClassName}
        />
      )}
    </Modal>
  );
}
